import { Injectable, inject } from '@angular/core';
import { EmailLogResendResult } from '@models/email-logs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { EmailLogsService } from '@services/email-logs.service';
import { ToastService } from '@services/toast.service';
import { firstValueFrom } from 'rxjs';

/**
 * Shared "resend a logged email" flow used by both the list and the detail view: confirm dialog →
 * resend request → success/error toast. Kept in one place so the two call sites can't drift.
 */
@Injectable({ providedIn: 'root' })
export class EmailLogResendService {
  private readonly emailLogsService = inject(EmailLogsService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  /**
   * Confirms then queues the resend. Resolves to the new outbox row's identifiers when it was queued,
   * or null when the user cancelled or the request failed (errors are surfaced as a toast — it never
   * rejects). `onStart` runs once the user confirms, just before the request fires; use it to drive an
   * in-flight indicator.
   *
   * `recipient` must be the recipient SNAPSHOT stored on the row (`toEmail` on a list item,
   * `recipientEmail` on the detail) — that is where the copy actually goes, not the customer's current
   * address. The API answers 202: the copy is queued and delivered later by the background dispatcher,
   * so a resolved result means "queued", not "delivered".
   */
  async confirmAndResend(
    id: number,
    recipient: string | null,
    onStart?: () => void,
  ): Promise<EmailLogResendResult | null> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('email-logs.resend.confirm-title'),
      prompt: recipient
        ? this.translate.instant('email-logs.resend.confirm-prompt', {
            recipient,
          })
        : this.translate.instant('email-logs.resend.confirm-prompt-no-recipient'),
    });
    if (!confirmed) return null;

    onStart?.();
    try {
      const result = await firstValueFrom(this.emailLogsService.resendEmailLog(id));
      this.toast.showSuccess(this.translate.instant('email-logs.resend.queued'));
      return result;
    } catch (err) {
      this.toast.showError(err);
      return null;
    }
  }
}
